<script setup lang="ts">
import { computed } from "vue";
import { RouterLink } from "vue-router";
import Icon from "@/design/primitives/Icon.vue";
import Tooltip from "@/design/primitives/Tooltip.vue";
import { formatCredits, formatDate } from "../api/money";
import { useCredits } from "../api/use-credits";
import { useBillingI18n } from "../i18n/use-billing-i18n";

/**
 * O saldo, na barra de cima — porque o dia em que ele zera é o dia em que a IA
 * para de responder, e ninguém abre a tela de cobrança para descobrir isso.
 *
 * ── POR QUE NA BARRA E NÃO SÓ EM `/billing` ───────────────────────────────
 *
 * A V2 aprendeu isso do jeito caro: o cliente descobria o saldo zerado pelo
 * silêncio do assistente, já com gente esperando do outro lado. Um número
 * discreto, sempre visível, é a única coisa que transforma "acabou" em "está
 * acabando". É a mesma leitura que a tela de créditos faz (`/v3/credits`,
 * mesma chave de consulta), então isto não custa uma requisição nova.
 *
 * ── POR QUE QUEM NÃO ADMINISTRA TAMBÉM VÊ ─────────────────────────────────
 *
 * Porque quem atende é quem sente primeiro. `GET /v3/credits` não pede papel; o
 * que pede é `/billing`, e por isso o número só vira LINK para quem pode abrir
 * aquela tela — mandar um atendente para um 403 seria pior do que não ter link.
 */
const props = defineProps<{
	/** Sem sessão não há saldo — e a consulta nem sai. */
	enabled: boolean;
	/** Se esta sessão pode abrir `/billing`, que é `minRole: "admin"`. */
	canOpen: boolean;
}>();

const { t } = useBillingI18n();

const { data } = useCredits(() => props.enabled);

/**
 * O estado, em quatro degraus — e um quinto que é não desenhar nada.
 *
 * "Sem plano e sem bônus" não é zero: é workspace que ainda não assinou, e para
 * esse a faixa de teste já fala. Um chip vermelho ali seria um alarme sobre algo
 * que ninguém contratou.
 */
const chip = computed(() => {
	const credits = data.value;
	if (!credits) return null;

	const total = credits.plan + credits.bonus;

	if (credits.blockedByAdmin) {
		return {
			icon: "hand" as const,
			label: t("credits.chip.blocked"),
			detail: t("credits.chip.blockedDetail"),
			tone: "border-danger/30 bg-danger-subtle text-danger",
		};
	}

	if (credits.blockedNoCredits || total <= 0) {
		if (credits.planMonthly <= 0 && !credits.blockedNoCredits) return null;
		return {
			icon: "hand" as const,
			label: t("credits.chip.paused"),
			detail: t("credits.chip.pausedDetail"),
			tone: "border-danger/30 bg-danger-subtle text-danger",
		};
	}

	if (credits.planMonthly <= 0 && credits.bonus <= 0) return null;

	const detail =
		t("credits.chip.detail", {
			plan: formatCredits(credits.plan),
			bonus: formatCredits(credits.bonus),
		}) +
		(credits.cycleEnd
			? ` ${t("credits.chip.renews", { date: formatDate(credits.cycleEnd) })}`
			: "");

	// O último décimo do plano é onde ainda dá tempo de comprar sem ninguém ficar
	// sem resposta — e é por isso que o aviso nasce aí, e não no zero.
	const low = credits.planMonthly > 0 && credits.plan <= credits.planMonthly * 0.1;

	return {
		icon: "bolt" as const,
		label: t("credits.chip.credits", { n: formatCredits(total) }),
		detail,
		tone: low
			? "border-warning/30 bg-warning-subtle text-warning"
			: "border-border bg-bg text-fg-muted hover:border-border-strong hover:text-fg",
	};
});

const CHIP_CLASS =
	"flex h-8 items-center gap-1.5 rounded-full border px-2.5 text-xs font-medium tabular-nums transition-colors";
</script>

<template>
	<Tooltip v-if="chip" :text="chip.detail">
		<RouterLink v-if="canOpen" to="/billing/credits" :class="[CHIP_CLASS, chip.tone]">
			<Icon :name="chip.icon" class="size-3.5" />
			<span>{{ chip.label }}</span>
		</RouterLink>
		<!-- Sem destino, sem cara de link: quem não administra lê o número e pronto. -->
		<span v-else :class="[CHIP_CLASS, chip.tone]">
			<Icon :name="chip.icon" class="size-3.5" />
			<span>{{ chip.label }}</span>
		</span>
	</Tooltip>
</template>
